import { useState, useEffect, useCallback } from "react";
import { LogOut, Inbox, FileEdit, FolderKanban, Trash2, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Logo } from "@/components/Logo";

const STATUS = [
  { value: "new", label: "Ny" },
  { value: "contacted", label: "Kontaktet" },
  { value: "quoted", label: "Tilbud sendt" },
  { value: "won", label: "Vundet" },
  { value: "closed", label: "Lukket" },
];
const statusLabel = (v) => STATUS.find((s) => s.value === v)?.label || v;
const statusColor = {
  new: "bg-blue-100 text-blue-800", contacted: "bg-amber-100 text-amber-800",
  quoted: "bg-purple-100 text-purple-800", won: "bg-green-100 text-green-800", closed: "bg-gray-200 text-gray-700",
};

const LoginScreen = () => {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="admin-login">
      <div className="bg-white border border-gray-200 rounded-sm p-10 max-w-md w-full text-center">
        <div className="flex justify-center mb-6"><Logo /></div>
        <h1 className="text-2xl font-bold text-gray-900">Admin-login</h1>
        <p className="mt-3 text-gray-600 text-sm">Kun ejeren har adgang til administrationen.</p>
        <button onClick={login} data-testid="google-login-btn" className="mt-8 w-full bg-[#FF5A00] text-white font-semibold px-6 py-3.5 rounded-sm hover:bg-[#E04F00] transition-colors">
          Log ind med Google
        </button>
      </div>
    </div>
  );
};

const Admin = () => {
  const [auth, setAuth] = useState(null); // null=checking, false=no, object=user
  const [tab, setTab] = useState("inquiries");

  const check = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setAuth(res.data.is_admin ? res.data : false);
      if (!res.data.is_admin) toast.error("Denne konto har ikke admin-adgang.");
    } catch {
      setAuth(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // handled by AuthCallback
    check();
  }, [check]);

  const logout = async () => {
    await api.post("/auth/logout");
    setAuth(false);
  };

  if (auth === null) return <div className="min-h-screen flex items-center justify-center text-gray-500">Indlæser...</div>;
  if (!auth) return <LoginScreen />;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 hidden sm:inline">{auth.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-[#FF5A00]"><LogOut size={16} /> Log ud</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { k: "inquiries", label: "Henvendelser", icon: Inbox },
            { k: "content", label: "Indhold & SEO", icon: FileEdit },
            { k: "projects", label: "Projektreferencer", icon: FolderKanban },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-semibold transition-colors ${tab === t.k ? "bg-[#FF5A00] text-white" : "bg-white border border-gray-200 text-gray-700 hover:border-gray-400"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "inquiries" && <Inquiries />}
        {tab === "content" && <ContentEditor />}
        {tab === "projects" && <Projects />}
      </div>
    </div>
  );
};

const Inquiries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const res = await api.get("/inquiries"); setItems(res.data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    await api.patch(`/inquiries/${id}`, { status });
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    toast.success("Status opdateret");
  };

  if (loading) return <p className="text-gray-500">Indlæser henvendelser...</p>;
  if (!items.length) return <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-gray-500" data-testid="inquiries-empty">Ingen henvendelser endnu.</div>;

  return (
    <div className="space-y-4" data-testid="inquiries-list">
      {items.map((i) => (
        <div key={i.id} className="bg-white border border-gray-200 rounded-sm p-6" data-testid={`inquiry-${i.id}`}>
          <div className="flex flex-wrap justify-between gap-4 items-start">
            <div>
              <h3 className="font-bold text-gray-900">{i.virksomhedsnavn}</h3>
              <p className="text-sm text-gray-500">{i.kontaktperson} · CVR {i.cvr || "-"}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm ${statusColor[i.status]}`}>{statusLabel(i.status)}</span>
              <select value={i.status} onChange={(e) => changeStatus(i.id, e.target.value)} data-testid={`status-select-${i.id}`} className="text-sm border border-gray-300 rounded-sm px-2 py-1">
                {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm text-gray-700">
            <p><a href={`tel:${i.telefon}`} className="text-[#FF5A00]">{i.telefon}</a></p>
            <p><a href={`mailto:${i.email}`} className="text-[#FF5A00]">{i.email}</a></p>
            <p>Adresse: {i.projektadresse || "-"}</p>
            <p>Startdato: {i.startdato || "-"}</p>
            <p>Størrelse: {i.projektstoerrelse || "-"}</p>
            <p>Modtaget: {new Date(i.created_at).toLocaleString("da-DK")}</p>
          </div>
          <p className="mt-4 text-gray-700 bg-gray-50 border border-gray-100 rounded-sm p-4 whitespace-pre-line">{i.beskrivelse}</p>
        </div>
      ))}
    </div>
  );
};

const ContentEditor = () => {
  const { content, setContent } = useContent();
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(content); }, [content]);
  if (!draft) return <p className="text-gray-500">Indlæser...</p>;

  const upd = (path, value) => {
    setDraft((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      const keys = path.split(".");
      let o = copy;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/content", draft);
      setContent(res.data);
      toast.success("Indhold gemt");
    } catch { toast.error("Kunne ikke gemme"); }
    finally { setSaving(false); }
  };

  const cls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5A00]";
  const Card = ({ title, children }) => (
    <div className="bg-white border border-gray-200 rounded-sm p-6">
      <h3 className="font-bold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
  const Row = ({ label, path, textarea, rows = 3 }) => {
    const keys = path.split(".");
    let v = draft; keys.forEach((k) => (v = v?.[k]));
    return (
      <div>
        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</label>
        {textarea
          ? <textarea rows={rows} className={cls} value={v || ""} onChange={(e) => upd(path, e.target.value)} data-testid={`content-${path.replace(/\./g, "-")}`} />
          : <input className={cls} value={v || ""} onChange={(e) => upd(path, e.target.value)} data-testid={`content-${path.replace(/\./g, "-")}`} />}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="content-editor">
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Kontaktoplysninger">
          <Row label="Virksomhedsnavn" path="contact.company_name" />
          <Row label="CVR" path="contact.cvr" />
          <Row label="Adresse" path="contact.address" />
          <Row label="Postnr. og by" path="contact.postal_city" />
          <Row label="Land" path="contact.country" />
          <Row label="Telefon" path="contact.phone" />
          <Row label="E-mail" path="contact.email" />
          <Row label="Åbningstider" path="contact.hours" />
        </Card>
        <div className="space-y-6">
          <Card title="Hero (forside)">
            <Row label="Overskrift" path="hero.headline" textarea />
            <Row label="Undertekst" path="hero.subtext" textarea />
          </Card>
          <Card title="SEO metadata">
            <Row label="Forside titel" path="seo.home_title" />
            <Row label="Forside beskrivelse" path="seo.home_desc" textarea />
          </Card>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Privatlivspolitik"><Row label="Tekst" path="privacy_text" textarea rows={12} /></Card>
        <Card title="Cookiepolitik"><Row label="Tekst" path="cookie_text" textarea rows={12} /></Card>
      </div>
      <button onClick={save} disabled={saving} data-testid="save-content-btn" className="bg-[#FF5A00] text-white font-semibold px-8 py-3 rounded-sm hover:bg-[#E04F00] transition-colors disabled:opacity-60">
        {saving ? "Gemmer..." : "Gem ændringer"}
      </button>
    </div>
  );
};

const emptyProject = { title: "", description: "", image_url: "", location: "", published: false };

const Projects = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyProject);
  const [editing, setEditing] = useState(null);

  const load = async () => { const res = await api.get("/projects/all"); setItems(res.data); };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Angiv titel");
    if (editing) await api.put(`/projects/${editing}`, form);
    else await api.post("/projects", form);
    setForm(emptyProject); setEditing(null); load();
    toast.success("Gemt");
  };
  const edit = (p) => { setForm(p); setEditing(p.id); };
  const del = async (id) => { await api.delete(`/projects/${id}`); load(); toast.success("Slettet"); };

  const cls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5A00]";

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="projects-editor">
      <div className="bg-white border border-gray-200 rounded-sm p-6">
        <h3 className="font-bold text-gray-900 mb-2">{editing ? "Rediger reference" : "Ny projektreference"}</h3>
        <p className="text-xs text-gray-500 mb-4">Referencer vises kun offentligt, når de er markeret som "Offentliggjort". Ingen referencer er aktive ved lancering.</p>
        <form onSubmit={save} className="space-y-4">
          <input className={cls} placeholder="Titel" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-title" />
          <input className={cls} placeholder="Placering (fx Randers)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="project-location" />
          <input className={cls} placeholder="Billede-URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="project-image" />
          <textarea rows={4} className={cls} placeholder="Beskrivelse" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-description" />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#FF5A00]" data-testid="project-published" /> Offentliggjort
          </label>
          <div className="flex gap-3">
            <button type="submit" data-testid="save-project-btn" className="inline-flex items-center gap-2 bg-[#FF5A00] text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-[#E04F00]"><Plus size={16} /> {editing ? "Opdater" : "Tilføj"}</button>
            {editing && <button type="button" onClick={() => { setForm(emptyProject); setEditing(null); }} className="text-sm text-gray-600 font-semibold">Annuller</button>}
          </div>
        </form>
      </div>

      <div className="space-y-3">
        {!items.length && <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-gray-500" data-testid="projects-empty">Ingen projektreferencer endnu.</div>}
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-gray-200 rounded-sm p-5 flex justify-between items-start gap-4" data-testid={`project-${p.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-gray-900">{p.title}</h4>
                <span className={`text-xs px-2 py-0.5 rounded-sm ${p.published ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>{p.published ? "Offentlig" : "Skjult"}</span>
              </div>
              <p className="text-sm text-gray-500">{p.location}</p>
              <p className="text-sm text-gray-600 mt-1">{p.description}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => edit(p)} className="text-gray-500 hover:text-[#FF5A00]" data-testid={`edit-project-${p.id}`}><FileEdit size={18} /></button>
              <button onClick={() => del(p.id)} className="text-gray-500 hover:text-red-600" data-testid={`delete-project-${p.id}`}><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Admin;
